import { Admin, Resource, CustomRoutes, fetchUtils, defaultTheme } from 'react-admin';
import simpleRestProvider from 'ra-data-simple-rest';
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import withValidations from "./withValidations";
import MyLayout from './MyLayout';
import { Route } from 'react-router-dom';
import { useState } from 'react';

import CompaniesList from './Components/CompaniesAdminstration/CompaniesList';
import CompanieShow from './Components/CompaniesAdminstration/CompaniesShow';

import CompaniesOperationsList from './Components/CompaniesOperations/CompaniesList';
import CompanieOperationsShow from './Components/CompaniesOperations/CompaniesShow';

import CompaniesReportsList from './Components/CompaniesReports/CompaniesList';
import CompanieReportsShow from './Components/CompaniesReports/CompaniesShow';

import RealStateList from './Components/RealState/RealStateList';
import RealStateEdit from './Components/RealState/RealStateShow';
import RealStateCreate from './Components/RealState/RealStateCreate';

import RealStateTypeList from './Components/RealStateType/RealStateTypeList';
import RealStateTypeEdit from './Components/RealStateType/RealStateTypeShow';
import RealStateTypeCreate from './Components/RealStateType/RealStateTypeCreate';

import DownPaymentToBeSettlemntList from './Components/DownPaymentToBeSettlement/DownPaymentList';
import DownPaymentToBeSettlemntShow from './Components/DownPaymentToBeSettlement/DownPaymentShow';

import DownPaymentsGeneralList from './Components/DownPaymentsGeneral/DownPaymentList';
import DownPaymentsGeneralShow from './Components/DownPaymentsGeneral/DownPaymentShow';
import DownPaymentsGeneralCreate from './Components/DownPaymentsGeneral/DownPaymentCreate';

import SettlementDownPaymentShow from './Components/SettlementsDownPayment/SettlementDownPaymentShow';
import SettlementDownPaymentCreate from './Components/SettlementsDownPayment/SettlementDownPaymentCreate';

import ReportsDownPaymentsFail from './Components/Reports/DownPaymentsFail';
import ReportsDownPaymentsPortfolio from './Components/Reports/DownPaymentsPortfolio';
import ReportsDownPaymentsPayment from './Components/Reports/DownPaymentsPayment';
import ReportsDownPaymentsAnalysis from './Components/Reports/DownPaymentsAnalysis';

import ProvidersList from './Components/Providers/ProvidersList';
import ProvidersCreate from './Components/Providers/ProvidersCreate';
import ProvidersShow from './Components/Providers/ProvidersShow';
import ProviderInvoicesList from './Components/Providers/ProviderInvoicesList';
import ProviderInvoiceCreate from './Components/Providers/ProviderInvoiceCreate';
import ProviderInvoiceShow from './Components/Providers/ProviderInvoiceShow';
import ProviderPayableInvoicesList from './Components/Providers/ProviderPayableInvoicesList';
import ProviderPayableInvoiceShow from './Components/Providers/ProviderPayableInvoiceShow';
import AccountingExpensesList from './Components/Providers/AccountingExpensesList';
import AccountingExpenseCreate from './Components/Providers/AccountingExpenseCreate';
import AccountingExpenseShow from './Components/Providers/AccountingExpenseShow';
import InventoryIntegrationsList from './Components/Providers/InventoryIntegrationsList';
import InventoryIntegrationCreate from './Components/Providers/InventoryIntegrationCreate';
import InventoryIntegrationShow from './Components/Providers/InventoryIntegrationShow';

import RequestAuthorizationsList from './Components/Providers/RequestAuthorizationsList';
import RequestAuthorizationShow from './Components/Providers/RequestAuthorizationShow';
import PrintRequestsList from './Components/Providers/PrintRequestsList';
import PrintRequestShow from './Components/Providers/PrintRequestShow';

import LogOut from './Components/Commons/Logout';

import States from './Components/Commons/States';
import { buildAuthorizationHeader } from './Components/Commons/AuthHeaders';

const domain = process.env.REACT_APP_URL_API ?? "";

// Color de marca (usado en Styles.jsx como '#191268') aplicado como primary
// del tema. El AppBar usa el color "secondary" del tema por defecto para su
// fondo (que si no se sobreescribe queda en el azul de fábrica #2196f3 que
// trae defaultLightTheme), así que acá se fuerza a blanco para la cabecera.
//
// typography.fontFamily: el resto de la app fuerza 'Gothic' campo por campo, componente
// por componente (ver Style en Styles.jsx, InputFieldCustom.jsx, etc.) -- pero cualquier
// texto que dependa del tema de MUI en vez de un estilo propio (ej. las notificaciones de
// react-admin, que son un Snackbar/Alert de MUI) no pasaba por ninguno de esos overrides y
// se quedaba con la tipografia por defecto de MUI (Roboto), aunque el body ya tiene
// 'Gothic' via index.css -- MUI aplica su propia fontFamily con mas especificidad que el
// font-family heredado del body. Con esto, cualquier componente de MUI que no tenga ya su
// propio fontFamily inline (como las notificaciones) usa 'Gothic' por default, igual que
// el resto del proyecto.
const theme = {
  ...defaultTheme,
  palette: {
    ...defaultTheme.palette,
    primary: {
      main: 'rgb(25, 18, 104)',
    },
    secondary: {
      main: '#ffffff',
    },
  },
  typography: {
    ...defaultTheme.typography,
    fontFamily: 'Gothic',
  },
};

const App = () => {
  const [module, setModule] = useState('Admin');

  const { getIdTokenClaims, getAccessTokenSilently } = useAuth0();

  const fetchJson = async (url, options = {}) => {
    if (!options.headers) {
      options.headers = new Headers({ Accept: 'application/json' });
    }
    const customHeaders = await (async () => {
      var c = await getIdTokenClaims();
      States.setUser(c?.nickname);
      return {
        user: c?.sub,
        username: c?.nickname,
        company: States.getCompany(),
        organization: States.getOrganizationId(),
        nit: States.getNIT(),
        productDownPayment : States.getProductDownPayment(),
        // Ver AuthHeaders.js: el "user" de arriba es un dato sin firmar, cualquiera podria
        // mandar otro. Este header si lo puede verificar la API (Auth0JwtMiddleware.cs).
        ...(await buildAuthorizationHeader(getAccessTokenSilently)),
      }
    })();

    for (var header in customHeaders) {
      options.headers.set(header, customHeaders[header]);
    }

    return fetchUtils.fetchJson(url, options);
  }

  const dataProvider = simpleRestProvider(domain, fetchJson)

  const myDataProvider = {
    ...dataProvider,
    delete: async (resource, params) => {
      const { id, previousData } = params;
      var c = await getIdTokenClaims();
      const url = `${domain}/${resource}/${id}`;
      const options = {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          user: c?.sub,
          username: c?.nickname,
          company: States.getCompany(),
          organization: States.getOrganizationId(),
          nit: States.getNIT(),
          ...(await buildAuthorizationHeader(getAccessTokenSilently)),
        },
        body: JSON.stringify(previousData),
      };

      return fetch(url, options)
        .then(async response => {
          if (!response.ok) {
            const error = await response.text();
            throw new Error(JSON.parse(error).error);
          }
          return response.json();
        })
        .then(json => ({
          data: json,
        }));
    },
    auth: async (resource, params) => {
      const { id, data } = params;
      var c = await getIdTokenClaims();
      const url = `${domain}/${resource}/${id}?action=${data.action}`;
      const options = {
        method: 'GET',
        headers: {
          user: c?.sub,
          username: c?.nickname,
          company: States.getCompany(),
          organization: States.getOrganizationId(),
          nit: States.getNIT(),
          ...(await buildAuthorizationHeader(getAccessTokenSilently)),
        },
      };

      return fetch(url, options)
        .then(response => {
          if (!response.ok) {
            throw new Error(response.status);
          }
          return response.json();
        })
        .then(json => ({
          data: json,
        }));
    },
  };

  return (
    <Admin
      layout={props => <MyLayout {...props} module={module} />}
      dataProvider={myDataProvider}
      theme={theme}
    >
      <CustomRoutes>
        <Route path="/logout" element={<LogOut />} />
        <Route path="/reports/downpayment/analysis" element={<ReportsDownPaymentsAnalysis />} />
      </CustomRoutes>
      <Resource name="company/administration" list={props => <CompaniesList {...props} setModule={setModule} />} edit={CompanieShow} />
      <Resource name="company/operations" list={props => <CompaniesOperationsList {...props} setModule={setModule} />} edit={CompanieOperationsShow} />
      <Resource name="company/reports" list={props => <CompaniesReportsList {...props} setModule={setModule} />} edit={CompanieReportsShow} />
      <Resource name="realstate" list={RealStateList} edit={RealStateEdit} create={RealStateCreate} />
      <Resource name="realstatetype" list={RealStateTypeList} edit={RealStateTypeEdit} create={RealStateTypeCreate} />
      <Resource name="downpayment/settlement" list={DownPaymentToBeSettlemntList} show={DownPaymentToBeSettlemntShow} />
      <Resource name="downpayment/general" list={DownPaymentsGeneralList} show={DownPaymentsGeneralShow} create={DownPaymentsGeneralCreate}/>
      <Resource name="settlement/downpayment" edit={SettlementDownPaymentShow} create={SettlementDownPaymentCreate} />
      <Resource name="reports/downpayment/fail" list={ReportsDownPaymentsFail} />
      <Resource name="reports/downpayment/portfolio" list={ReportsDownPaymentsPortfolio} />
      <Resource name="reports/downpayment/payments" list={ReportsDownPaymentsPayment} />
      <Resource name="customer/downpayment"/>
      <Resource name="realstate/downpayments/product"/>
      {/* react-router v6+ ranks routes por especificidad de sus segmentos estaticos,
          no por orden de declaracion (a diferencia de v5). "provider/invoice/*" tiene
          mas segmentos estaticos que "provider/*" y por lo tanto siempre gana el match
          para /provider/invoice/..., sin importar el orden. Se mantiene este orden solo
          por legibilidad/documentacion de la intencion original. */}
      <Resource name="provider/invoice" list={ProviderInvoicesList} create={ProviderInvoiceCreate} show={ProviderInvoiceShow} />
      <Resource name="provider/invoicetopay" list={ProviderPayableInvoicesList} show={ProviderPayableInvoiceShow} />
      <Resource name="provider/accountingexpense" list={AccountingExpensesList} create={AccountingExpenseCreate} show={AccountingExpenseShow} />
      <Resource name="provider/inventoryintegration" list={InventoryIntegrationsList} create={InventoryIntegrationCreate} show={InventoryIntegrationShow} />
      <Resource name="provider" list={ProvidersList} create={ProvidersCreate} show={ProvidersShow} />
      <Resource name="bank/requestauthorization" list={RequestAuthorizationsList} show={RequestAuthorizationShow} />
      <Resource name="bank/printrequests" list={PrintRequestsList} show={PrintRequestShow} />
    </Admin>
  )
}

export default withAuthenticationRequired(withValidations(App));